import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setSearchTerm as setSearchViewSearchTerm } from '@repo/redux/actions/search';
import { CircledArrowIcon } from '@repo/icons';
import {
  navigateToSettings,
  selectArrayOfSelectedContacts,
  selectIsMultiselectView,
  selectSelectedContact,
  selectSelectedContactBatch,
} from '@repo/redux/actions/ui';
import { selectActiveGroupView } from '@repo/redux/actions/groupView';
import { selectGroupCollection } from '@repo/redux/actions/groups';
import renderOption, { MenuOption } from './Option';
import {
  filterMenuSection,
  searchableActionSectionsForState,
  searchableGroupSectionForCollection,
  searchableNavSection,
} from './locallySearchableSections';
import { useAuthContext } from '@repo/hooks';
import analytics from '@repo/utils/helpers/analytics';
import { useContactSearch } from '@repo/hooks';
import { useContactLimitExceeded } from '@app/Context/hooks/use-contact-limit-exceeded';
import { SearchableOptionsModal } from '@repo/components/modals';
import styles from './CommandBar.module.scss';
import { selectContactGetter } from '@repo/redux/actions/contacts';
import { useWorkspaceContext } from '@repo/hooks';
import { selectActiveArchiveView } from '@repo/redux/reducers/ui/archiveView';
import { selectActiveDuplicatesView } from '@repo/redux/reducers/ui/duplicatesView';

type MenuOptionsList = MenuOption[];
export type MenuSection = null | { title: string; suggestions: MenuOptionsList };

export const CommandBarModal = ({
  closeModal,
  overlayClassName,
  skipAutoFocus,
  id,
}: {
  closeModal: () => void;
  overlayClassName?: string;
  skipAutoFocus?: boolean;
  id?: string;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, cleanupAndLogout, contactIsRestricted, getEntitlementValue, contactIsSelf } =
    useAuthContext();
  const { userIsAdmin } = useWorkspaceContext();
  const contactLimitExceeded = useContactLimitExceeded();

  // Relevant app state for building Command Bar state
  const selectedContact = useSelector(selectSelectedContact);
  const selectedContactBatch = useSelector(selectSelectedContactBatch);
  const selectedContactArray = useSelector(selectArrayOfSelectedContacts);
  const groupView = useSelector(selectActiveGroupView);
  const isMultiselectView = useSelector(selectIsMultiselectView);
  const archiveView = useSelector(selectActiveArchiveView);
  const duplicatesView = useSelector(selectActiveDuplicatesView);

  const getContact = useSelector(selectContactGetter);

  // For search/navigation of user's groups
  const groupCollection = useSelector(selectGroupCollection);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [setContactSearchTerm, contactSearchState] = useContactSearch({ resultCount: 4 });

  // Menu sections, filtered by the current search term. If a section has 0 suggestions, we skip rendering
  const [actionSections, setActionSections] = useState<Array<MenuSection>>([]);
  const [contactSection, setContactSection] = useState<MenuSection>(null);
  const [groupSection, setGroupSection] = useState<MenuSection>(null);
  const [navSection, setNavSection] = useState<MenuSection>(null);
  // list of menu sections
  const [allFilteredSections, setAllFilteredSections] = useState([]);

  const searchableActionSections = useMemo(
    () =>
      searchableActionSectionsForState(
        {
          selectedContact,
          selectedContactBatch,
          selectedContactArray,
          groupView,
          archiveView,
          duplicatesView,
          isMultiselectView,
          location,
        },
        dispatch,
        navigate,
        user,
        cleanupAndLogout,
        userIsAdmin,
        getEntitlementValue,
        contactIsRestricted,
        getContact,
        contactLimitExceeded,
        contactIsSelf,
      ),
    [
      selectedContact,
      selectedContactBatch,
      selectedContactArray,
      groupView,
      archiveView,
      isMultiselectView,
      navigate,
      getContact,
      userIsAdmin,
    ],
  );
  const searchableGroupSection = useMemo(
    () => searchableGroupSectionForCollection(groupCollection),
    [groupCollection],
  );

  const filterActionSections = useCallback(
    (term: string) => {
      return searchableActionSections
        .map((section) => filterMenuSection(section, term, true))
        .filter((x) => x);
    },
    [searchableActionSections],
  );

  const filterGroupSection = useCallback(
    (term: string): MenuSection => filterMenuSection(searchableGroupSection, term),
    [searchableGroupSection],
  );

  const filterNavSection = useCallback(
    (term: string): MenuSection => filterMenuSection(searchableNavSection, term),
    [],
  );

  const onSuggestionSelected = (suggestion) => {
    analytics.track('Command Bar Used');
    closeModal();

    switch (suggestion.type) {
      case 'group':
        return navigate(`/group/${suggestion.group.slug || suggestion.group.id}`);

      case 'contact':
        return navigate(`/contact/${suggestion.contact.objectID}`);

      case 'nav':
        if (suggestion.text === 'Settings') {
          return dispatch(navigateToSettings());
        }
        return navigate(suggestion.to);

      default:
        return suggestion.action();
    }
  };

  const setActionsSuggestionsForSearchTerm = useCallback(
    (term: string) => setActionSections(filterActionSections(term)),
    [],
  );

  const handleSearch = useCallback((value) => {
    setActionsSuggestionsForSearchTerm(value);
    setGroupSection(filterGroupSection(value));
    setNavSection(filterNavSection(value));
  }, []);

  useEffect(() => {
    if (location.pathname != '/search') {
      setContactSearchTerm(searchTerm);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (location.pathname == '/search') {
      return;
    }

    let returnedContacts = contactSearchState.results
      .asContactArray(getContact)
      .slice(0, 3)
      .map((contact) => ({ contact, type: 'contact' })) as MenuOptionsList;

    if (returnedContacts.length == 0) {
      setContactSection(null);
      return;
    }

    if (returnedContacts.length == 3) {
      returnedContacts = [
        ...returnedContacts,
        {
          type: 'cta',
          title: 'See all results',
          action: () => {
            dispatch(setSearchViewSearchTerm(searchTerm));
            navigate('/search');
          },
          inlineIcon: CircledArrowIcon,
        },
      ];
    }

    setContactSection({
      title: `Search results for "${searchTerm}"`,
      suggestions: returnedContacts,
    });
  }, [searchTerm, contactSearchState.results, getContact]);

  useEffect(() => {
    setAllFilteredSections(
      actionSections.concat([groupSection, navSection, contactSection]).filter((x) => x),
    );
  }, [actionSections, contactSection, groupSection, navSection]);

  useEffect(() => {
    setActionsSuggestionsForSearchTerm(searchTerm);
  }, [searchableActionSections]);

  return (
    <SearchableOptionsModal
      searchTerm={searchTerm}
      closeModal={closeModal}
      setSearchTerm={setSearchTerm}
      optionSections={allFilteredSections}
      onOptionSelected={onSuggestionSelected}
      handleSearch={handleSearch}
      renderOption={renderOption}
      optionClassName={styles.option}
      overlayClassName={overlayClassName}
      skipAutoFocus={skipAutoFocus}
      id={id}
    />
  );
};
